// A checklist kept in a markdown file, as editors and Claude Code write one:
// "- [ ] Ship billing" and "- [x] Fix the menu" (or with * or +, indented or
// not). The Project file widget shows the file's tasks and changes only the
// line it means, so everything else in the file stays exactly as it was.

export interface FileTask {
  /** the line it is on, from 0 */
  line: number;
  text: string;
  done: boolean;
  /** how far in it is, for a task under a task */
  depth: number;
}

const TASK = /^(\s*)([-*+])\s+\[([ xX])\]\s?(.*)$/;

/** The file's lines, and the newline it uses, so a change writes it back the same way. */
function linesOf(text: string): { lines: string[]; nl: string } {
  const nl = text.includes("\r\n") ? "\r\n" : "\n";
  return { lines: text.split(/\r?\n/), nl };
}

export function readFileTasks(text: string): FileTask[] {
  const out: FileTask[] = [];
  linesOf(text).lines.forEach((l, i) => {
    const m = TASK.exec(l);
    if (m) out.push({ line: i, text: m[4].trim(), done: m[3] !== " ", depth: Math.floor(m[1].replace(/\t/g, "  ").length / 2) });
  });
  return out;
}

/** The file with one task ticked or unticked: only its box changes. null when that line is no longer that task. */
export function setFileTask(text: string, task: FileTask, done: boolean): string | null {
  const { lines, nl } = linesOf(text);
  const m = TASK.exec(lines[task.line] ?? "");
  if (!m || m[4].trim() !== task.text) return null;
  lines[task.line] = `${m[1]}${m[2]} [${done ? "x" : " "}] ${m[4]}`.replace(/\s+$/, "");
  return lines.join(nl);
}

/** The file with a task added after its last task, or at its end when it has none. */
export function addFileTask(text: string, task: string): string {
  const words = task.replace(/\s+/g, " ").trim();
  if (!words) return text;
  const { lines, nl } = linesOf(text);
  const tasks = readFileTasks(text);
  const last = tasks.at(-1);
  if (last) {
    const m = TASK.exec(lines[last.line])!;
    lines.splice(last.line + 1, 0, `${m[1]}${m[2]} [ ] ${words}`);
    return lines.join(nl);
  }
  // a file that ends with a newline keeps one
  const trailing = lines.length > 1 && lines[lines.length - 1] === "";
  if (trailing) lines.splice(lines.length - 1, 0, `- [ ] ${words}`);
  else lines.push(`- [ ] ${words}`);
  return lines.join(nl);
}
